import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_KEYS } from "./query-keys";
import { toast } from "sonner";

export const useAllSessionPlayers = (userId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.allSessionPlayers(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("session_players")
        .select(`
          *,
          sessions!inner(*),
          players(*)
        `)
        .eq("sessions.created_by", userId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });
};

export const usePlayerSessionPlayers = (playerId?: string) => {
  return useQuery({
    queryKey: ['session-players', 'player', playerId],
    staleTime: 30_000,
    queryFn: async () => {
      if (!playerId) return [];
      const { data, error } = await supabase
        .from("session_players")
        .select(`
          *,
          sessions(*)
        `)
        .eq("player_id", playerId)
        .order('sessions(date)', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!playerId,
  });
};

export const useSessionsRanks = (sessionIds: string[]) => {
  return useQuery({
    queryKey: ['session-players', 'ranks', sessionIds],
    staleTime: 60_000,
    queryFn: async () => {
      if (!sessionIds || sessionIds.length === 0) return [];

      // Build batches of 50 to stay within URL length limits
      const batches: string[][] = [];
      for (let i = 0; i < sessionIds.length; i += 50) {
        batches.push(sessionIds.slice(i, i + 50));
      }

      // Fetch all batches in parallel instead of sequentially
      const results = await Promise.all(
        batches.map(batch =>
          supabase
            .from("session_players")
            .select("session_id, player_id, net_amount")
            .in("session_id", batch)
            .then(({ data, error }) => {
              if (error) throw error;
              return data || [];
            })
        )
      );

      return results.flat();
    },
    enabled: sessionIds.length > 0,
  });
};

export const useSessionResults = (sessionId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.sessionResults(sessionId),
    queryFn: async () => {
      if (!sessionId) return [];
      const { data, error } = await supabase
        .from("session_players")
        .select(`
          *,
          players(*)
        `)
        .eq("session_id", sessionId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!sessionId,
  });
};

export const useUpdateSessionResults = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, results }: { sessionId: string; results: any[] }) => {
      for (const res of results) {
        const { error } = await supabase
          .from('session_players')
          .update({
            buy_ins: res.buyIns,
            cash_out: res.cashOut,
            net_amount: res.net
          })
          .eq('session_id', sessionId)
          .eq('player_id', res.id);

        if (error) throw error;
      }
    },
    onMutate: async ({ sessionId, results }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
      const previousResults = queryClient.getQueryData(QUERY_KEYS.sessionResults(sessionId));

      queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId), (old: any[] | undefined) => {
        if (!old) return [];
        return old.map((row) => {
          const res = results.find((r: any) => r.id === row.player_id);
          if (res) {
            return {
              ...row,
              buy_ins: res.buyIns,
              cash_out: res.cashOut,
              net_amount: res.net
            };
          }
          return row;
        });
      });

      return { previousResults };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(variables.sessionId) });
      queryClient.invalidateQueries({ queryKey: ['session-players', 'all'] });
      toast.success("Session results updated successfully");
    },
    onError: (error: any, variables, context) => {
      if (context?.previousResults) {
        queryClient.setQueryData(QUERY_KEYS.sessionResults(variables.sessionId), context.previousResults);
      }
      toast.error(`Error updating session results: ${error.message}`);
    },
  });
};

export const useAddBuyInMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, playerId, currentBuyIns, buyInUnit }: { sessionId: string; playerId: string; currentBuyIns: number; buyInUnit?: number }) => {
      const updateData: any = { buy_ins: currentBuyIns + 1 };
      
      // If we have buyInUnit, we can also update net_amount optimistically/locally
      // but here we are in the mutationFn which is the server call.
      // The server will usually handle net_amount if it's a computed column or if we provide it.
      // In this app, it seems net_amount is manually updated.
      
      if (buyInUnit !== undefined) {
        // We'll update net_amount based on existing cash_out if available
        // but it's safer to let the server handle it or update it in the endSession.
      }

      const { data, error } = await supabase
        .from('session_players')
        .update(updateData)
        .eq('session_id', sessionId)
        .eq('player_id', playerId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ sessionId, playerId, buyInUnit }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
      const previousResults = queryClient.getQueryData(QUERY_KEYS.sessionResults(sessionId));

      queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId), (old: any[] | undefined) => {
        if (!old) return [];
        return old.map((row) => {
          if (row.player_id === playerId) {
            const newBuyIns = (row.buy_ins || 0) + 1;
            const updates: any = { buy_ins: newBuyIns };
            if (buyInUnit !== undefined) {
              updates.net_amount = Math.round(((row.cash_out || 0) - (newBuyIns * buyInUnit)) * 100) / 100;
            }
            return { ...row, ...updates };
          }
          return row;
        });
      });

      return { previousResults };
    },
    onError: (err, variables, context) => {
      if (context?.previousResults) {
        queryClient.setQueryData(QUERY_KEYS.sessionResults(variables.sessionId), context.previousResults);
      }
      toast.error("Failed to add buy-in");
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(variables.sessionId) });
    },
  });
};

export const useRemoveBuyInMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, playerId, currentBuyIns, buyInUnit }: { sessionId: string; playerId: string; currentBuyIns: number; buyInUnit?: number }) => {
      if (currentBuyIns <= 1) return;
      const { data, error } = await supabase
        .from('session_players')
        .update({ buy_ins: currentBuyIns - 1 })
        .eq('session_id', sessionId)
        .eq('player_id', playerId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ sessionId, playerId, buyInUnit }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
      const previousResults = queryClient.getQueryData(QUERY_KEYS.sessionResults(sessionId));

      queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId), (old: any[] | undefined) => {
        if (!old) return [];
        return old.map((row) => {
          if (row.player_id === playerId && row.buy_ins > 1) {
            const newBuyIns = row.buy_ins - 1;
            const updates: any = { buy_ins: newBuyIns };
            if (buyInUnit !== undefined) {
              updates.net_amount = Math.round(((row.cash_out || 0) - (newBuyIns * buyInUnit)) * 100) / 100;
            }
            return { ...row, ...updates };
          }
          return row;
        });
      });

      return { previousResults };
    },
    onError: (err, variables, context) => {
      if (context?.previousResults) {
        queryClient.setQueryData(QUERY_KEYS.sessionResults(variables.sessionId), context.previousResults);
      }
      toast.error("Failed to remove buy-in");
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(variables.sessionId) });
    },
  });
};

export const useUpdateCashOutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, playerId, cashOut, buyInUnit }: { sessionId: string; playerId: string; cashOut: number; buyInUnit?: number }) => {
      const { data, error } = await supabase
        .from('session_players')
        .update({ cash_out: cashOut })
        .eq('session_id', sessionId)
        .eq('player_id', playerId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ sessionId, playerId, cashOut, buyInUnit }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
      const previousResults = queryClient.getQueryData(QUERY_KEYS.sessionResults(sessionId));

      queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId), (old: any[] | undefined) => {
        if (!old) return [];
        return old.map((row) => {
          if (row.player_id === playerId) {
            const updates: any = { cash_out: cashOut };
            if (buyInUnit !== undefined) {
              updates.net_amount = Math.round((cashOut - ((row.buy_ins || 0) * buyInUnit)) * 100) / 100;
            }
            return { ...row, ...updates };
          }
          return row;
        });
      });

      return { previousResults };
    },
    onError: (err, variables, context) => {
      if (context?.previousResults) {
        queryClient.setQueryData(QUERY_KEYS.sessionResults(variables.sessionId), context.previousResults);
      }
      toast.error("Failed to update cash-out");
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(variables.sessionId) });
    },
  });
};

export const useAddPlayerToSessionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, playerId }: { sessionId: string; playerId: string }) => {
      const { data, error } = await supabase
        .from('session_players')
        .insert({
          session_id: sessionId,
          player_id: playerId,
          buy_ins: 1,
        })
        .select(`
          *,
          players(*)
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ sessionId, playerId }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
      const previousResults = queryClient.getQueryData(QUERY_KEYS.sessionResults(sessionId));

      // Try to find player info in the players cache
      const playersData = queryClient.getQueriesData<any[]>({ queryKey: ['players'] });
      let playerInfo = null;
      for (const [key, data] of playersData) {
        if (data) {
          playerInfo = data.find(p => p.id === playerId);
          if (playerInfo) break;
        }
      }

      queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId), (old: any[] | undefined) => {
        const newRow = {
          session_id: sessionId,
          player_id: playerId,
          buy_ins: 1,
          cash_out: 0,
          net_amount: 0,
          players: playerInfo || { id: playerId, name: 'Loading...' }
        };
        return [...(old || []), newRow];
      });

      return { previousResults };
    },
    onError: (error: any, variables, context) => {
      if (context?.previousResults) {
        queryClient.setQueryData(QUERY_KEYS.sessionResults(variables.sessionId), context.previousResults);
      }
      toast.error(`Error adding player: ${error.message}`);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(variables.sessionId) });
    },
  });
};

export const useRemovePlayerFromSessionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, playerId }: { sessionId: string; playerId: string }) => {
      const { error } = await supabase
        .from('session_players')
        .delete()
        .eq('session_id', sessionId)
        .eq('player_id', playerId);
      if (error) throw error;
    },
    onMutate: async ({ sessionId, playerId }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
      const previousResults = queryClient.getQueryData(QUERY_KEYS.sessionResults(sessionId));
      queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId), (old: any[] | undefined) =>
        (old || []).filter(row => row.player_id !== playerId)
      );
      return { previousResults };
    },
    onError: (err, variables, context) => {
      if (context?.previousResults) {
        queryClient.setQueryData(QUERY_KEYS.sessionResults(variables.sessionId), context.previousResults);
      }
      toast.error("Failed to remove player");
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(variables.sessionId) });
    },
  });
};

export const useAddPlayersToSessionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, playerIds }: { sessionId: string; playerIds: string[] }) => {
      const { data, error } = await supabase
        .from('session_players')
        .insert(
          playerIds.map((pid) => ({
            session_id: sessionId,
            player_id: pid,
            buy_ins: 1,
          }))
        )
        .select(`
          *,
          players(*)
        `);

      if (error) throw error;
      return data;
    },
    onMutate: async ({ sessionId, playerIds }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.sessionResults(sessionId) });
      const previousResults = queryClient.getQueryData(QUERY_KEYS.sessionResults(sessionId));

      const playersData = queryClient.getQueriesData<any[]>({ queryKey: ['players'] });
      
      queryClient.setQueryData(QUERY_KEYS.sessionResults(sessionId), (old: any[] | undefined) => {
        const newRows = playerIds.map(pid => {
          let playerInfo = null;
          for (const [key, data] of playersData) {
            if (data) {
              playerInfo = data.find(p => p.id === pid);
              if (playerInfo) break;
            }
          }
          return {
            session_id: sessionId,
            player_id: pid,
            buy_ins: 1,
            cash_out: 0,
            net_amount: 0,
            players: playerInfo || { id: pid, name: 'Loading...' }
          };
        });
        return [...(old || []), ...newRows];
      });

      return { previousResults };
    },
    onError: (error: any, variables, context) => {
      if (context?.previousResults) {
        queryClient.setQueryData(QUERY_KEYS.sessionResults(variables.sessionId), context.previousResults);
      }
      toast.error(`Error adding players: ${error.message}`);
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionResults(variables.sessionId) });
    },
  });
};
